import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Autocomplete, TextField,
  CircularProgress, Box
} from '@mui/material';
import { LibraryAdd } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { subjectService } from '../../services/subjectService';
import { curriculumApi } from '../../services/sessions/curriculumApi';
import { Subject } from '../../types';

interface AssignSubjectsDialogProps {
  open: boolean;
  sessionId: string;
  classId: string;
  className: string;
  /** null = "common subjects" node; set = section-specific node */
  sectionInstanceId: string | null;
  sectionName?: string;
  /** Subject IDs already assigned to this slot — hidden from picker. */
  alreadyAssignedIds: string[];
  onClose: () => void;
  onAssigned: () => void;
}

const AssignSubjectsDialog: React.FC<AssignSubjectsDialogProps> = ({
  open, sessionId, classId, className, sectionInstanceId, sectionName, alreadyAssignedIds, onClose, onAssigned
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [picked, setPicked] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { subjects: list } = await subjectService.getSubjects({ isActive: true, limit: 500 });
        if (cancelled) return;
        setSubjects(list);
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not load subjects', { variant: 'error' });
      } finally {
        if (!cancelled) { setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [open, enqueueSnackbar]);

  // Reset on close.
  useEffect(() => { if (!open) setPicked([]); }, [open]);

  const candidates = subjects.filter(s => !alreadyAssignedIds.includes(String(s._id)));

  const submit = async () => {
    if (picked.length === 0) return;
    setSubmitting(true);
    try {
      await curriculumApi.bulkAssign({
        assignments: picked.map(s => ({
          sessionId,
          classId,
          sectionInstanceId,
          subjectId: String(s._id)
        }))
      });
      enqueueSnackbar(
        `${picked.length} subject${picked.length === 1 ? '' : 's'} added`,
        { variant: 'success' }
      );
      onAssigned();
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not add subjects', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const slotLabel = sectionInstanceId == null ? 'Common (all sections)' : `Section: ${sectionName}`;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <LibraryAdd color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={600}>Add subjects</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          <strong>{className}</strong> · {slotLabel}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
        ) : (
          <Stack spacing={2}>
            <Autocomplete<Subject, true>
              multiple
              options={candidates}
              getOptionLabel={(o) => o.name}
              value={picked}
              onChange={(_, v) => setPicked(v)}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pick subjects from your catalog"
                  placeholder={candidates.length ? 'Type to search…' : 'No subjects available'}
                />
              )}
              noOptionsText={candidates.length === 0 ? 'All subjects in your catalog are already assigned here' : 'No matches'}
            />
            {sectionInstanceId == null && (
              <Typography variant="caption" color="text.secondary">
                Common subjects apply to every section of this class. To assign a subject only to a specific section,
                close this dialog and pick that section in the left tree.
              </Typography>
            )}
            {sectionInstanceId != null && (
              <Typography variant="caption" color="text.secondary">
                These subjects will be section-specific. A subject can't be both common AND section-specific in the same class —
                we'll surface a clear error if there's a clash.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={submit}
          variant="contained"
          disableElevation
          disabled={submitting || picked.length === 0}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Adding…' : `Add ${picked.length || ''} subject${picked.length === 1 ? '' : 's'}`.trim()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignSubjectsDialog;
